import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../common/Modal";
import DocumentUpload from "./DocumentUpload";

const DocumentUploadModal = ({
  isOpen,
  onClose,
  onUploadComplete,
  proposalId,
}) => {
  const [uploadResults, setUploadResults] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleUploadComplete = (results) => {
    setUploadResults(results);

    // Call parent upload complete handler (to refresh sidebar), but DO NOT auto-close
    onUploadComplete?.(results);
  };

  const handleUploadError = (error) => {
    console.error("Upload error:", error);
    // Error handling is done within DocumentUpload component
  };

  const handleClose = () => {
    setUploadResults([]);
    setUploading(false);
    onClose();
  };

  const hasSuccessfulUploads = uploadResults.some((r) => r.success);
  const hasFailedUploads = uploadResults.some((r) => !r.success);

  // Don't render anything if modal should not be open
  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title="Upload Documents"
      onClose={handleClose}
      hideFooter={true}
      widthClassName="max-w-4xl"
      showCloseX={true}
      closeOnBackdropClick={!uploading}
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FontAwesomeIcon
              icon="info-circle"
              className="text-blue-600 mt-0.5"
            />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Upload Guidelines:</p>
              <ul className="space-y-1">
                <li>
                  • Supported formats: PDF, Word (.doc/.docx), Excel
                  (.xls/.xlsx), PowerPoint (.ppt/.pptx)
                </li>
                <li>• Maximum file size: 50MB per file</li>
                <li>
                  • Files will be automatically processed and made available for
                  AI analysis
                </li>
                <li>
                  • Document types help organize and categorize your files
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Upload Component */}
        <DocumentUpload
          proposalId={proposalId}
          onUploadComplete={handleUploadComplete}
          onUploadError={handleUploadError}
        />

        {/* Upload Results */}
        {uploadResults.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-slate-900">Upload Results:</h4>

            {hasSuccessfulUploads && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FontAwesomeIcon icon="check" className="text-emerald-600" />
                  <span className="font-medium text-emerald-800">
                    Successfully Uploaded (
                    {uploadResults.filter((r) => r.success).length})
                  </span>
                </div>
                <div className="space-y-1">
                  {uploadResults
                    .filter((r) => r.success)
                    .map((result, index) => (
                      <div key={index} className="text-sm text-emerald-700">
                        ✓ {result.file}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {hasFailedUploads && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FontAwesomeIcon
                    icon="exclamation-triangle"
                    className="text-red-600"
                  />
                  <span className="font-medium text-red-800">
                    Failed Uploads (
                    {uploadResults.filter((r) => !r.success).length})
                  </span>
                </div>
                <div className="space-y-1">
                  {uploadResults
                    .filter((r) => !r.success)
                    .map((result, index) => (
                      <div key={index} className="text-sm text-red-700">
                        ✗ {result.file}: {result.error}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {hasSuccessfulUploads && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  🎉 Documents uploaded successfully! They will be processed and
                  made available for AI analysis within a few minutes. You can
                  now reference them in your chats.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
          <button
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-50"
          >
            {hasSuccessfulUploads ? "Done" : "Cancel"}
          </button>

          {hasSuccessfulUploads && (
            <button
              onClick={() => {
                onUploadComplete?.(uploadResults);
                handleClose();
              }}
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <FontAwesomeIcon icon="check" />
              <span>Continue to Workspace</span>
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DocumentUploadModal;
